import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Loader2, ArrowRight } from 'lucide-react';
import { apiGet } from '../data/api';
import t from '../i18n/it';

const STATUS_TABS = [
  { key: '', label: t.matches.all },
  { key: 'live', label: t.matches.live },
  { key: 'scheduled', label: t.matches.scheduled },
  { key: 'finished', label: t.matches.finished },
];

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">
      {t.common.tennis}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">
      {t.common.table_tennis}
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === 'live')
    return (
      <span className="flex items-center gap-1 text-xs text-red-400 font-medium">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" />
        {t.status.live}
      </span>
    );
  if (status === 'scheduled')
    return (
      <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/15 text-amber-400">
        {t.status.scheduled}
      </span>
    );
  return (
    <span className="px-1.5 py-0.5 text-xs rounded bg-zinc-700/40 text-zinc-500">
      {t.status.finished}
    </span>
  );
}

function MatchCard({ match }) {
  return (
    <Link
      to={`/incontri/${match.id}`}
      data-testid={`match-card-${match.id}`}
      className="block rounded-sm p-4 transition-all hover:-translate-y-px"
      style={{ background: '#18181B', border: '1px solid #27272A' }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.borderColor = '#3F3F46')
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.borderColor = '#27272A')
      }
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SportBadge sport={match.sport} />
          {match.surface && (
            <span className="text-xs text-zinc-600">{match.surface}</span>
          )}
          {match.format && (
            <span className="text-xs text-zinc-700">{match.format.replace('_', ' ')}</span>
          )}
        </div>
        <StatusBadge status={match.status} />
      </div>

      <div className="text-center my-3">
        <div className="flex items-center justify-center gap-3">
          <span
            className={`font-semibold text-sm ${
              match.winner_id === match.player1_id
                ? 'text-emerald-400'
                : 'text-zinc-200'
            }`}
          >
            {match.player1_name}
          </span>
          <span
            className="text-zinc-600 text-xs font-mono"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            vs
          </span>
          <span
            className={`font-semibold text-sm ${
              match.winner_id === match.player2_id
                ? 'text-emerald-400'
                : 'text-zinc-200'
            }`}
          >
            {match.player2_name}
          </span>
        </div>
        {match.status === 'scheduled' && (
          <div className="mt-2 text-xs text-zinc-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {new Date(match.start_time).toLocaleString('it-IT', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>

      <div
        className="flex items-center justify-between pt-2.5"
        style={{ borderTop: '1px solid #27272A' }}
      >
        <div>
          <p className="text-xs text-zinc-500 truncate max-w-[200px]">
            {match.tournament_name}
          </p>
          {match.round && (
            <p className="text-xs text-zinc-600">{match.round}</p>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-emerald-400">
          {t.common.details} <ArrowRight size={11} />
        </div>
      </div>
    </Link>
  );
}

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusTab, setStatusTab] = useState('');
  const [sportFilter, setSportFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusTab) params.append('status', statusTab);
    if (sportFilter) params.append('sport', sportFilter);
    const query = params.toString() ? `?${params}` : '';
    apiGet(`/matches${query}`)
      .then(setMatches)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [statusTab, sportFilter]);

  return (
    <div className="space-y-5 fade-in">
      <div>
        <h1
          className="text-4xl font-bold text-zinc-50 tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t.matches.title}
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">{t.matches.subtitle}</p>
      </div>

      {/* Tabs + filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div
          className="flex rounded-sm overflow-hidden"
          style={{ border: '1px solid #27272A' }}
        >
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key || 'all'}`}
              onClick={() => setStatusTab(tab.key)}
              className={`px-4 py-2 text-sm transition-colors ${
                statusTab === tab.key
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <select
          data-testid="matches-filter-sport"
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <option value="">{t.signals.all_sports}</option>
          <option value="tennis">{t.common.tennis}</option>
          <option value="table_tennis">{t.common.table_tennis}</option>
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
          <Loader2 size={18} className="animate-spin" />
          <span>{t.common.loading}</span>
        </div>
      ) : error ? (
        <div className="text-red-400 text-center py-10">{t.common.error}</div>
      ) : matches.length === 0 ? (
        <div className="text-zinc-600 text-center py-16 text-sm">
          <Calendar size={32} className="mx-auto mb-3 opacity-30" />
          {t.matches.no_matches}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {matches.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      )}
    </div>
  );
}
